import { useEffect, useState } from "react";
import { MagnifyingGlass, ArrowsDownUp, X } from "@phosphor-icons/react";

// Deals Board v2 — Top toolbar: Search + Sort.
// Responsive at 1440px (full width row) and mobile (wraps to 2 rows).
export default function DealsToolbar({
  search,
  onSearchChange,
  sort,
  onSortChange,
  sortOptions,
  totalCount,
  filteredCount,
  onNewDeal,
  canCreate,
  onToggleFilters,
  filtersActive,
  filtersOpen,
}) {
  const [local, setLocal] = useState(search || "");

  // debounce search updates by 250ms
  useEffect(() => {
    const t = setTimeout(() => {
      if (local !== search) onSearchChange(local);
    }, 250);
    return () => clearTimeout(t);
  }, [local, search, onSearchChange]);

  return (
    <div
      data-testid="deals-toolbar"
      className="sticky top-0 z-30 bg-white border border-slate-200 px-4 py-3 mb-4 flex flex-wrap items-center gap-3"
    >
      {/* Search */}
      <div className="relative flex-1 min-w-[220px] max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          data-testid="deals-search-input"
          type="text"
          placeholder="Search deals by title or contact…"
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          className="w-full pl-9 pr-9 py-2 text-sm border border-slate-300 rounded-none outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] bg-white"
        />
        {local && (
          <button
            data-testid="deals-search-clear"
            aria-label="Clear search"
            onClick={() => setLocal("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Sort */}
      <div className="flex items-center gap-2">
        <ArrowsDownUp size={14} className="text-slate-500" />
        <select
          data-testid="deals-sort-by"
          value={sort.by}
          onChange={(e) => onSortChange({ ...sort, by: e.target.value })}
          className="text-sm border border-slate-300 rounded-none py-2 px-2 outline-none focus:border-[#002FA7] bg-white"
        >
          {sortOptions.map((o) => (
            <option key={o.value} value={o.value}>
              Sort: {o.label}
            </option>
          ))}
        </select>
        <button
          data-testid="deals-sort-dir"
          onClick={() => onSortChange({ ...sort, dir: sort.dir === "desc" ? "asc" : "desc" })}
          className="text-xs px-2 py-2 border border-slate-300 rounded-none uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50"
          title={sort.dir === "desc" ? "Descending — click for Ascending" : "Ascending — click for Descending"}
        >
          {sort.dir === "desc" ? "↓ Desc" : "↑ Asc"}
        </button>
      </div>

      {/* Filters toggle */}
      <button
        data-testid="deals-toggle-filters"
        onClick={onToggleFilters}
        className={`text-xs px-3 py-2 border rounded-none uppercase tracking-wider font-bold transition-colors ${
          filtersActive
            ? "border-[#002FA7] bg-[#002FA7] text-white"
            : "border-slate-300 text-slate-700 hover:bg-slate-50"
        }`}
      >
        {filtersOpen ? "Hide Filters" : "Filters"} {filtersActive ? "•" : ""}
      </button>

      {/* Count */}
      <div className="ml-auto flex items-center gap-3">
        <span data-testid="deals-count" className="text-xs text-slate-500">
          {filteredCount === totalCount ? `${totalCount} deals` : `${filteredCount} of ${totalCount}`}
        </span>
        {canCreate && (
          <button
            data-testid="deals-new-btn"
            onClick={onNewDeal}
            className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-4 py-2 text-sm font-medium"
          >
            + New Deal
          </button>
        )}
      </div>
    </div>
  );
}
